import Microgame from '../engine/Microgame.js';

export default class TunerGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Règle !`, en: `Tune!`, es: `¡Sintoniza!` },
            controls: { fr: `Tourne le bouton (glisse/scrolle) pour régler la radio`, en: `Turn the dial (swipe/scroll) to tune the radio`, es: `Gira el dial (desliza/desplaza) para sintonizar` },
            objective: { fr: `Trouve la bonne fréquence pour avoir du son clair`, en: `Find the clear frequency to stop the static`, es: `Encuentra la frecuencia correcta para tener sonido claro` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" stroke-width="2"><path d="M2 12h5l2.5-5 5 10 2.5-5H22"/></svg>`,
            actions: [
                { type: 'wait', duration: 260 },
                { type: 'swipe', fromX: 50, fromY: 85, toX: 34, toY: 77, duration: 700 },
                { type: 'wait', duration: 520 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.dial = null;
        this.playerWave = null;
        this.targetWave = null;
        this.currentAngle = 0;
        this.targetAngle = this.isPlayback ? 240 : (45 + Math.random() * 270); // 45 to 315 deg
        
        this._isVictoryAnimating = false;
        this._frozenTimerValue = 0;

        // Difficulty scaling
        const speedMult = engine.speedMultiplier || 1.0;
        // Tolerance: 15° @ x1, -> 7° @ x4 (Slightly more lenient than before)
        this.tolerance = 15 - (speedMult - 1) * (8 / 3); 
        
        const adjustedSpeed = 1 + (speedMult - 1) * (1.14 / 3);
        this.duration = 4500 * (speedMult / adjustedSpeed);
        this.stabilizationTimer = 0;
        this.stabilizationGoal = 200; // Keep the precision feel
    }

    setup() {
        this.engine.ui.instruction.textContent = "TUNE!";

        // Screen Area
        const screen = document.createElement('div');
        screen.style.position = 'absolute';
        screen.style.width = '300px';
        screen.style.height = '150px';
        screen.style.background = 'rgba(0,0,0,0.4)';
        screen.style.border = `2px solid ${this.secondaryColor}`;
        screen.style.top = '30%';
        screen.style.left = '50%';
        screen.style.transform = 'translate(-50%, -50%)';
        screen.style.borderRadius = '10px';
        screen.style.overflow = 'hidden';
        this.container.appendChild(screen);

        // SVG Waves
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute('viewBox', '0 0 300 150');
        svg.style.width = '100%';
        svg.style.height = '100%';
        screen.appendChild(svg);

        // Target Wave (Secondary)
        this.targetWave = document.createElementNS("http://www.w3.org/2000/svg", "path");
        this.targetWave.setAttribute('stroke', this.secondaryColor);
        this.targetWave.setAttribute('stroke-width', '2');
        this.targetWave.setAttribute('fill', 'none');
        this.targetWave.setAttribute('opacity', '0.4');
        this.targetWave.setAttribute('stroke-dasharray', '8,4');
        svg.appendChild(this.targetWave);

        // Player Wave (Primary)
        this.playerWave = document.createElementNS("http://www.w3.org/2000/svg", "path");
        this.playerWave.setAttribute('stroke', this.primaryColor);
        this.playerWave.setAttribute('stroke-width', '4');
        this.playerWave.setAttribute('fill', 'none');
        this.playerWave.style.filter = `drop-shadow(0 0 5px ${this.primaryColor})`;
        svg.appendChild(this.playerWave);

        // Dial (Primary)
        this.dialContainer = document.createElement('div');
        this.dialContainer.style.position = 'absolute';
        this.dialContainer.style.bottom = '15%';
        this.dialContainer.style.left = '50%';
        this.dialContainer.style.width = '120px';
        this.dialContainer.style.height = '120px';
        this.dialContainer.style.transform = 'translateX(-50%)';
        this.container.appendChild(this.dialContainer);

        this.dial = document.createElement('div');
        this.dial.style.width = '100%';
        this.dial.style.height = '100%';
        this.dial.style.background = this.primaryColor;
        this.dial.style.borderRadius = '50%';
        this.dial.style.boxShadow = `0 0 20px ${this.primaryColor}`;
        this.dial.style.cursor = 'grab';
        this.dial.style.touchAction = 'none';
        this.dialContainer.appendChild(this.dial);

        // Frequency Number Text (Dynamic)
        this.freqText = document.createElement('div');
        this.freqText.style.position = 'absolute';
        this.freqText.style.top = '58%';
        this.freqText.style.left = '50%';
        this.freqText.style.transform = 'translateX(-50%)';
        this.freqText.style.fontSize = '32px';
        this.freqText.style.fontWeight = '900';
        this.freqText.style.color = this.secondaryColor;
        this.freqText.style.fontFamily = 'monospace';
        this.freqText.style.zIndex = '5';
        this.container.appendChild(this.freqText);

        // Target Reference (Faint)
        const targetFreq = 1 + (this.targetAngle / 360) * 5;
        const targetRef = document.createElement('div');
        targetRef.style.position = 'absolute';
        targetRef.style.top = '48%';
        targetRef.style.left = '50%';
        targetRef.style.transform = 'translateX(-50%)';
        targetRef.style.fontSize = '14px';
        targetRef.style.color = 'rgba(255,255,255,0.2)';
        targetRef.style.fontWeight = 'bold';
        targetRef.textContent = `TARGET: ${targetFreq.toFixed(1)} MHz`;
        this.container.appendChild(targetRef);

        // Dial Indicator
        const indicator = document.createElement('div');
        indicator.style.position = 'absolute';
        indicator.style.top = '10px';
        indicator.style.left = '50%';
        indicator.style.width = '8px';
        indicator.style.height = '25px';
        indicator.style.background = this.secondaryColor;
        indicator.style.borderRadius = '4px';
        indicator.style.transform = 'translateX(-50%)';
        this.dial.appendChild(indicator);

        // Render target wave once
        this.updateWave(this.targetWave, this.targetAngle);
        // Initial player wave
        this.updateWave(this.playerWave, this.currentAngle);

        // Events
        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.dial.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    update(dt) {
        if (this._isVictoryAnimating) {
            this.engine.timer = this._frozenTimerValue;
            return;
        }

        if (!this.active || this.won) return;

        // Check Tune
        const diff = Math.abs(this.currentAngle - this.targetAngle);
        const finalDiff = Math.min(diff, 360 - diff);

        if (finalDiff < this.tolerance) {
            this.stabilizationTimer += dt;
            
            // Visual feedback: Getting clearer/brighter as we stabilize
            const progress = this.stabilizationTimer / this.stabilizationGoal;
            this.playerWave.setAttribute('stroke', `rgb(${255 * progress}, 255, 255)`);
            this.playerWave.style.filter = `drop-shadow(0 0 ${5 + 10 * progress}px white)`;
            this.playerWave.setAttribute('opacity', 0.5 + progress * 0.5);

            if (this.stabilizationTimer >= this.stabilizationGoal) {
                this.handleSuccess();
            }
        } else {
            this.stabilizationTimer = 0;
            // Back to primary color
            this.playerWave.setAttribute('stroke', this.primaryColor);
            this.playerWave.style.filter = `drop-shadow(0 0 5px ${this.primaryColor})`;
            this.playerWave.setAttribute('opacity', '1');
        }

        // Update Frequency Number Display
        const freq = 1 + (this.currentAngle / 360) * 5;
        this.freqText.textContent = freq.toFixed(1) + " MHz";
        
        // Color transition based on proximity (0 to 1)
        // We use a range of 60 degrees for the color transition
        const colorProgress = Math.max(0, 1 - (finalDiff / 60));
        this.freqText.style.color = this.interpolateColors(this.secondaryColor, this.primaryColor, colorProgress);
        this.freqText.style.transform = `translateX(-50%) scale(${1 + colorProgress * 0.2})`;
        this.freqText.style.textShadow = `0 0 ${10 * colorProgress}px ${this.primaryColor}`;
    }

    // Helper to interpolate between two hex/css colors
    interpolateColors(color1, color2, factor) {
        // Simple hex/name to rgb conversion or just use CSS variables if possible
        // Since primaryColor and secondaryColor are dynamic, we'll use a simple approach
        // For now, let's just use opacity/mix if we can, or hardcode a simple linear interpolation if they are hex
        // Given project style, we usually have hex strings. Let's assume hex.
        const hex = (c) => {
            const h = c.replace('#', '');
            return h.length === 3 ? h.split('').map(s => s + s).join('') : h;
        };
        const r1 = parseInt(hex(color1).substring(0, 2), 16);
        const g1 = parseInt(hex(color1).substring(2, 4), 16);
        const b1 = parseInt(hex(color1).substring(4, 6), 16);
        
        const r2 = parseInt(hex(color2).substring(0, 2), 16);
        const g2 = parseInt(hex(color2).substring(2, 4), 16);
        const b2 = parseInt(hex(color2).substring(4, 6), 16);
        
        const r = Math.round(r1 + factor * (r2 - r1));
        const g = Math.round(g1 + factor * (g2 - g1));
        const b = Math.round(b1 + factor * (b2 - b1));
        
        return `rgb(${r}, ${g}, ${b})`;
    }

    updateWave(path, angle) {
        // Reduced frequency range for better visibility: Map angle (0-360) to frequency (1-6)
        const freq = 1 + (angle / 360) * 5;
        let d = "M 0 75 ";
        for (let x = 0; x <= 300; x += 5) {
            const y = 75 + Math.sin(x * 0.05 * freq) * 50;
            d += `L ${x} ${y} `;
        }
        path.setAttribute('d', d);
    }

    onDown(e) {
        if (!this.active || this.won || this.lost) return;
        this.isDragging = true;
        this.dial.style.cursor = 'grabbing';
        
        const rect = this.dial.getBoundingClientRect();
        this.centerX = rect.left + rect.width / 2;
        this.centerY = rect.top + rect.height / 2;
    }

    onMove(e) {
        if (!this.isDragging || !this.active || this.won) return;

        const dx = e.clientX - this.centerX;
        const dy = e.clientY - this.centerY;
        let angle = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
        if (angle < 0) angle += 360;

        this.currentAngle = angle;
        this.dial.style.transform = `rotate(${angle}deg)`;
        this.updateWave(this.playerWave, angle);
    }

    handleSuccess() {
        if (this.won) return;
        this.won = true;
        this._isVictoryAnimating = true;
        this._frozenTimerValue = this.engine.timer;

        // Visual Polish
        this.playerWave.setAttribute('stroke-width', '8');
        this.playerWave.style.filter = `drop-shadow(0 0 15px white)`;
        this.playerWave.setAttribute('stroke', '#fff');
        this.dial.style.transform = `rotate(${this.targetAngle}deg) scale(1.1)`;
        this.dial.style.boxShadow = `0 0 40px white`;
        
        // Final Snap
        this.updateWave(this.playerWave, this.targetAngle);

        setTimeout(() => {
            this._isVictoryAnimating = false;
            this.win();
        }, 800);
    }

    onUp() {
        this.isDragging = false;
        if (this.dial) this.dial.style.cursor = 'grab';
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
